import React from 'react';

interface AnimatedGradientBackgroundProps {
  children: React.ReactNode;
  variant?: 'dark' | 'gradient' | 'light';
}

export const AnimatedGradientBackground: React.FC<AnimatedGradientBackgroundProps> = ({ children, variant = 'gradient' }) => {
  // If light mode, just render children with standard light background (or transparent if handled by parent)
  // The user requested: "claro que es el que esta ahora" -> standard app background
  if (variant === 'light') {
    return (
      <div className="absolute inset-0 w-full h-full overflow-hidden bg-white text-gray-900 transition-colors duration-500">
        <style>{`
                    @keyframes gradient-xy-light {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                    .animate-gradient-xy-light {
                        background-size: 400% 400%;
                        animation: gradient-xy-light 15s ease infinite;
                    }
                    @keyframes float {
                        0% { transform: translateY(0px); }
                        50% { transform: translateY(-20px); }
                        100% { transform: translateY(0px); }
                    }
                    .animate-float-slow { animation: float 6s ease-in-out infinite; }
                    .animate-float-medium { animation: float 4s ease-in-out infinite; }
                `}</style>

        {/* Main Light Gradient Layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 animate-gradient-xy-light opacity-90" />

        {/* Light Pastel Blobs */}
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-float-slow" />
        <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-float-medium" />
        <div className="absolute top-[40%] left-[60%] w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-float-slow" style={{ animationDelay: '1s' }} />

        {/* Subtle Noise for texture */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 brightness-100 contrast-150 mix-blend-overlay"></div>

        <div className="relative z-10 w-full h-full">
          {children}
        </div>
      </div>
    );
  }

  // If dark mode, simpler dark background without the crazy animation
  if (variant === 'dark') {
    return (
      <div className="absolute inset-0 w-full h-full bg-gray-900 text-white transition-colors duration-500">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-800" />
        <div className="relative z-10 w-full h-full">
          {children}
        </div>
      </div>
    );
  }

  // Gradient animation mode (User's provided code)
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden bg-gray-900 text-white transition-all duration-500">
      {/* 
        Injecting styles directly to handle the specific keyframes for the gradient animation 
        without needing an external CSS file or complex Tailwind config.
      */}
      <style>{`
        @keyframes gradient-xy {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient-xy {
          background-size: 400% 400%;
          animation: gradient-xy 15s ease infinite;
        }
        
        /* Floating shapes for extra depth */
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
        .animate-float-slow {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-medium {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>

      {/* Main Gradient Layer: Cool tones (Indigo, Purple, Pink) */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 animate-gradient-xy opacity-80" />

      {/* Secondary Mesh/Blob Layers for texture */}
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float-slow" />
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-yellow-300 rounded-full mix-blend-overlay filter blur-3xl opacity-30 animate-float-medium" />
      <div className="absolute top-[40%] left-[60%] w-72 h-72 bg-pink-400 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-float-slow" style={{ animationDelay: '1s' }} />

      {/* Grid Overlay for Modern Tech Feel */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay"></div>

      {/* Content */}
      <div className="relative z-10 w-full h-full">
        {children}
      </div>
    </div>
  );
};
